import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios';
import ProductCard from '../../ProductCard';
import Glide from '@glidejs/glide';

interface ProductRecomendationsProps {
  dataSet: {
    productId: number;
    baseUrl: string;
    limit: number;
    title: string;
    fromText: string;
    colourSwatches: string;
    roastText: string;
    tasteText: string;
    quickAddText: string;
    quickAddPlus: string;
    excludedHandles: string;
  }
}

const ProductRecommendations = (props: ProductRecomendationsProps): JSX.Element => {
  const [products, setProducts] = useState([]);
  const { 
    dataSet: { 
      baseUrl, 
      productId, 
      limit, 
      title, 
      fromText, 
      colourSwatches, 
      roastText, 
      tasteText, 
      quickAddText, 
      quickAddPlus,
      excludedHandles
    } 
  } = props;

  const excludedHandlesArr: string[] = excludedHandles.split(', ');

  const glideEl = useRef();
  const glideOptions = {
    type: 'carousel',
    perView: 3,
    perTouch: false,
    touchRatio: 0.5,
    dragThreshold: 50,
    gap: 0,
    peek: {
      before: 0,
      after: 0
    },
    breakpoints: {
      '1440': {
        perView: 3,
        peek: {
          before: 0,
          after: 0
        },
      },
      '1060': { 
        perView: 2, 
        perTouch: 1, 
        gap: 0,
        peek: {
          before: 0,
          after: 0
        }
      },
      '768': {
        perView: 1.25,
        perTouch: 1,
        peek: {
          before: 0,
          after: 0
        }
      },
    },
  }
  
  useEffect(() => {
    /*
      Get the maximum number of recommended products from the Recommendations API instead of using limit here (which is 10 right now)
      This is to avoid issues like this:
      - If limit were set to 4, the API would return 4 products
      - 2 of them are to be excluded and filtered out
      - In the end we only get two recommended products instead of 4
    */
    axios.get(`${baseUrl}.json?product_id=${productId}`)
      .then((res) => {
        const d = res.data;
        if (d && d.products) {
          d.products = d.products.filter(p => !excludedHandlesArr.includes(p.handle));
          setProducts(d.products.slice(0, limit));
        }
      })
      .catch((e) => {
        console.error(e)
      });
  }, []);

  useEffect(() => {
    let slider = null;

    if (products.length > 0) {
      slider = new Glide(glideEl.current, glideOptions);
      slider.mount();
    } 
    
    return () => slider?.destroy();
  }, [glideEl, products]);

  return (
    products && products.length > 0 ? (
      <section className="cl-product-card-container grid-list margin-t-80 glide--ltr flex flex-column al-glidify relative"
        ref={glideEl}>
        <div className="container relative">
          <header>
            <h2 className="heading-2 cl-product-title">{title}</h2>
          </header>
          <div className="glide__track" data-glide-el="track">
            <div className="glide__slides">
              {
                products.map((product, i) =>
                  <ProductCard
                    product={product}
                    showSelect={false}
                    fromText={fromText}
                    colourSwatches={colourSwatches}
                    roastText={roastText}
                    tasteText={tasteText}
                    quickAddText={quickAddText}
                    quickAddPlus={quickAddPlus}
                    key={`product-recommendation-${i}`} />
                    
                )
              }
            </div>
          </div>
          <div className="controls flex align-center gap-small" data-glide-el="controls">
            <span className="glide__arrow glide__arrow--left" data-glide-dir="<" data-desktop-arrow>
              <span className="visually-hidden">Previous Slide</span>
            </span>
            <span className="glide__arrow glide__arrow--right" data-glide-dir=">" data-desktop-arrow>
              <span className="visually-hidden">Next Slide</span>
            </span>
          </div>
        </div>
      </section>
    ) : null
  )
};

export default ProductRecommendations
